import React, {useState} from 'react';
import {Icon} from '@iconify/react';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {useDebounce} from 'use-debounce';
import Button from '../../../../../incase-domain/components/button/Button';
import {
    isRoomSlugConflictError,
    RoomSlugParam,
    useCheckRoomSlugAvailable,
    useCreateRoomSlug,
} from '../../../../../service/hooks/useRoomSlug';
import ParamPresets from '../../../PresEditPage/components/settings/SlugSettings/ParamPresets';
import {hasInvalidRedirectPreset} from '../../../../../utils/redirect';
import {SLUG_MAX, validateSlugFormat} from '../../../PresEditPage/components/settings/SlugSettings/slugValidation';
import {QUERY_KEYS} from '../../../../../utils/queryKeys';
import {RequestParam} from '../../../../../incase-domain/requestParams';

interface AddRoomSlugFormProps {
    roomId: string;
    presenterCode?: string;
    addLabel: string;
    requestParams: RequestParam[];
    showParams?: boolean;
    defaultParams?: RoomSlugParam[];
}

function AddRoomSlugForm({
    roomId,
    presenterCode,
    addLabel,
    requestParams,
    showParams = true,
    defaultParams = [],
}: AddRoomSlugFormProps): JSX.Element {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [slug, setSlug] = useState('');
    const [debouncedSlug] = useDebounce(slug, 400);
    const [params, setParams] = useState<RoomSlugParam[]>(defaultParams);
    const [formatError, setFormatError] = useState<string | null>(null);

    const {mutate: create, isLoading: isCreating} = useCreateRoomSlug();

    const shouldCheck = open && !!slug && !formatError && slug === debouncedSlug;
    const {data: available, isFetching: checking} = useCheckRoomSlugAvailable(shouldCheck ? debouncedSlug : '');

    const handleCreate = (): void => {
        if (formatError || available !== true || slug !== debouncedSlug) return;
        create(
            {roomId, slug, presenterCode, params},
            {
                onSuccess: () => {
                    toast.success(t('toasts.successfullySaved'));
                    setOpen(false);
                    setSlug('');
                    setParams(defaultParams);
                    setFormatError(null);
                    queryClient.invalidateQueries(QUERY_KEYS.roomSlug.byRoom(roomId));
                },
                onError: (error) => {
                    toast.error(t(isRoomSlugConflictError(error) ? 'roomsPage.settings.shortLink.taken' : 'toasts.errorOccurred'));
                },
            }
        );
    };

    if (!open) {
        return (
            <Button type="button" variant="ghost" color="info" size="sm" onClick={() => setOpen(true)} leading="solar:add-circle-linear">
                {addLabel}
            </Button>
        );
    }

    const isSaveDisabled =
        !slug || !!formatError || available !== true || slug !== debouncedSlug || hasInvalidRedirectPreset(params) || isCreating;

    return (
        <div className="c-settingsItem c-slugSettings__item">
            <div className="c-slugSettings__row">
                <div className="c-slugSettings__inputWrap">
                    <div className="c-sidepanel__input">
                        <input
                            type="text"
                            value={slug}
                            onChange={(e) => {
                                const val = e.target.value.toLowerCase();
                                setSlug(val);
                                setFormatError(validateSlugFormat(val));
                            }}
                            placeholder={t('roomsPage.settings.shortLink.slugPlaceholder')}
                            maxLength={SLUG_MAX}
                        />
                    </div>
                    {checking && (
                        <span className="c-slugSettings__statusIcon">
                            <Icon icon="solar:refresh-linear" width={14} />
                        </span>
                    )}
                    {!checking && slug && !formatError && available === true && (
                        <span className="c-slugSettings__statusIcon c-slugSettings__statusIcon--success">
                            <Icon icon="solar:check-circle-linear" width={14} />
                        </span>
                    )}
                    {!checking && slug && (available === false || !!formatError) && (
                        <span className="c-slugSettings__statusIcon c-slugSettings__statusIcon--error">
                            <Icon icon="solar:close-circle-linear" width={14} />
                        </span>
                    )}
                </div>
                <Button
                    type="button"
                    variant="solid"
                    color="info"
                    size="sm"
                    onClick={handleCreate}
                    disabled={isSaveDisabled}
                    loading={isCreating}
                >
                    {t('common.save')}
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                        setOpen(false);
                        setSlug('');
                        setParams(defaultParams);
                        setFormatError(null);
                    }}
                >
                    {t('common.cancel')}
                </Button>
            </div>

            {formatError && <span className="is-error c-slugSettings__hint--error">{formatError}</span>}
            {!formatError && slug && available === false && (
                <span className="is-error c-slugSettings__hint--error">{t('roomsPage.settings.shortLink.taken')}</span>
            )}
            {!formatError && slug && available === true && (
                <span className="c-slugSettings__hint--success">{t('roomsPage.settings.shortLink.available')}</span>
            )}
            {!slug && <span className="c-slugSettings__hint">{t('roomsPage.settings.shortLink.slugHint')}</span>}

            {showParams && requestParams?.length > 0 && (
                <div className="c-slugSettings__params">
                    <p className="c-slugSettings__paramsTitle">{t('roomsPage.settings.shortLink.params')}</p>
                    <ParamPresets requestParams={requestParams} value={params} onChange={setParams} />
                </div>
            )}
        </div>
    );
}

export default AddRoomSlugForm;
